import {
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import { useEsriMapContext } from '../EsriMapContext';
import type { SelectedAsset } from '../types';
import EsriUtils from '../utils/esriHelpers';
import { resolveSelectionType } from '../utils/assetCategory';
import { MapFunction, type MapSelectedAssets, type SelectionOptions } from '@Map/types';
import {
	boundsAreNotInitialized,
	convertCoordsToBounds,
	functionalityEnabled,
} from '@Map/utils';
import { geojsonToArcGIS } from '@terraformer/arcgis';
import { GeometryUnion } from '@arcgis/core/geometry/types';
import {
	extractLngLatPositions,
	type ValidGeometry,
} from '@Map/extractLngLatPositions';
import useAddPinsToMap, {
	centersForDropPinsFromGeometries,
} from './useAddPinToMap';
import * as locator from '@arcgis/core/rest/locator';

const WORLD_GEOCODER_URL =
	'https://geocode.arcgis.com/arcgis/rest/services/World/GeocodeServer';
const GEOCODER_DROP_PIN_LAYER_ID = 'geocoder-drop-pin';
const GEOCODER_PREVIEW_LAYER_ID = 'geocoder-preview-pin';

// Concentric teal circles matching Mapbox DropPin.markerElement() (#00ABD1).
const PREVIEW_PIN_SVG = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(
	'<svg xmlns="http://www.w3.org/2000/svg" width="45" height="45" viewBox="0 0 45 45">' +
		'<circle cx="22.5" cy="22.5" r="22.5" fill="#00ABD1" fill-opacity="0.18"/>' +
		'<circle cx="22.5" cy="22.5" r="15" fill="#00ABD1" fill-opacity="0.3"/>' +
		'<circle cx="22.5" cy="22.5" r="3.5" fill="#00ABD1"/>' +
		'</svg>',
)}`;

export type GeocoderPopup = {
	features?: Array<{
		place_name?: string;
		/** Exact click coordinates — shown in the coordinates link. */
		center: [number, number];
		/** Geocoded address location — may differ slightly from the click point. */
		addressCenter?: [number, number];
	}>;
	point?: { x: number; y: number };
};

const shouldZoomOnClick = (
	featureCount: number,
	zoomLevel: number,
	selectionOptions: SelectionOptions,
) => {
	const hasFeatures = featureCount > 0;
	const tooManyFeatures = featureCount > selectionOptions.maxFeatures;
	const zoomLevelLow = zoomLevel < selectionOptions.minZoom;
	return hasFeatures && (tooManyFeatures || zoomLevelLow);
};

export const useSelection = (options: {
	selectionOptions: SelectionOptions;
	selectedAssets?: MapSelectedAssets;
}) => {
	const {
		view,
		layerSelectionApi: layerApi,
		displayMode,
		isDrawing,
	} = useEsriMapContext();
	const addPinsToMap = useAddPinsToMap();
	const addGeocoderPin = useAddPinsToMap({
		layerId: GEOCODER_DROP_PIN_LAYER_ID,
		pinColor: '#768d95',
	});
	const addPreviewPin = useAddPinsToMap({
		layerId: GEOCODER_PREVIEW_LAYER_ID,
		pinSvg: PREVIEW_PIN_SVG,
		pinWidth: '45px',
		pinHeight: '45px',
		pinYOffset: '0',
	});
	const geocoderPinShownRef = useRef(false);
	const [geocoderPopupProps, setGeocoderPopupProps] = useState<GeocoderPopup>({});

	const clearGeocoderPopup = useCallback(() => {
		setGeocoderPopupProps({});
		addGeocoderPin(null);
		addPreviewPin(null);
		geocoderPinShownRef.current = false;
	}, [addGeocoderPin, addPreviewPin]);

	const setPreviewPin = useCallback(
		(center: [number, number]) => {
			addPreviewPin([center]);
		},
		[addPreviewPin],
	);

	const clearPreviewPin = useCallback(() => {
		addPreviewPin(null);
	}, [addPreviewPin]);

	const viewRef = useRef(view);
	viewRef.current = view;

	const selectionOptions = options.selectionOptions;

	const [selectedAssets, setSelectedAssets] = useState<SelectedAsset[]>([]);
	const [highlightedAssets, setHighlightedAssets] = useState<SelectedAsset[]>(
		[],
	);

	// Callbacks registered by the consumer
	const onAssetClickCb = useRef<((asset: SelectedAsset) => void) | null>(
		null,
	);
	const onAssetHoverCb = useRef<
		((asset: SelectedAsset | null) => void) | null
	>(null);
	const lastHoveredAssetKey = useRef<string | null>(null);

	const cursorRequestIdRef = useRef<number>(0);

	// Splits the 'assetType#assetId' composite id used by tile assets.
	const parseAssetIdentity = useCallback((id: string) => {
		if (!id || !id.includes('#')) {
			return { assetId: id, assetType: undefined as string | undefined };
		}
		const [assetType, assetId] = id.split('#');
		return { assetId, assetType };
	}, []);

	const enrichSelectedAsset = useCallback(
		(a: SelectedAsset): SelectedAsset => {
			const layerName =
				a.layerName || layerApi?.getLayerDisplayName(a.layerId);

			const ax = a as SelectedAsset & {
				name?: string;
				workspaceId?: string;
				facilityType?: string;
				srcType?: string;
				type?: string;
				sequence?: unknown;
				sensorId?: string;
				priority?: string;
				createDate?: string;
			};
			// Fetch the underlying feature's properties when we need to backfill
			// display fields OR when the selection lacks both an explicit
			// assetType and its own srcType — i.e. id-only selections from the
			// controlled `selectedAssets` prop / URL deep-links — so we can read
			// the feature's authoritative srcType (mirrors Mapbox Feature.assetType).
			// sensorId/priority/createDate only ever exist on alerts — gate them
			// on type so non-alert assets don't trigger a lookup they never need.
			const isAlert = ax.type === 'alert' || a.assetType === 'alert';
			const missingCommonFields =
				ax.name == null || ax.workspaceId == null || ax.facilityType == null;
			const missingAlertFields =
				isAlert &&
				(ax.sensorId == null ||
					ax.priority == null ||
					ax.createDate == null);
			const needsBackfill = missingCommonFields || missingAlertFields;
			// A generic 'asset' counts as unresolved: it is what an earlier pass
			// settles on when the layer had not loaded yet, and what
			// GeoJsonDataService stamps on incidents (I360P-1354).
			const needsType =
				(!a.assetType || a.assetType === 'asset') && !ax.srcType;
			// GeoJsonDataService stamps a feature's own `properties.id` with the
			// raw backend id, never the `type#id` composite selections use — so
			// the lookup has to search by the id's raw half, or it can never
			// match and `props` stays undefined forever (I360P-1357).
			const { assetId: rawAssetId, assetType: idPrefix } =
				parseAssetIdentity(a.id);
			const props =
				needsBackfill || needsType
					? layerApi?.getFeaturePropertiesById(a.layerId, rawAssetId)
					: undefined;

			const srcType = ax.srcType ?? (props?.srcType as string | undefined);
			const featureType = ax.type ?? (props?.type as string | undefined);
			// resolveSelectionType derives the pane `type` from the feature's own
			// srcType/type + resolved assetType, never from the previously
			// computed `type` — see the note on resolveSelectionType (I360I-1399).
			const { assetType, type } = resolveSelectionType({
				assetType: a.assetType,
				srcType,
				featureType,
				idPrefix,
				sequence: ax.sequence ?? props?.sequence,
				layerAssetType: layerApi?.getLayerAssetType(a.layerId),
			});

			return {
				...a,
				layerName,
				sourceId: a.sourceId ?? layerApi?.getLayerSourceId(a.layerId),
				assetType,
				type,
				name: ax.name ?? (props?.name as string | undefined),
				workspaceId:
					ax.workspaceId ?? (props?.workspaceId as string | undefined),
				facilityType:
					ax.facilityType ??
					(props?.facilityType as string | undefined),
				// Backfilled for the same reason as the display fields above: an
				// id-only incident selection has no sequence of its own, and the
				// Incidents pane tab renders it as the incident's id (I360P-1354).
				sensorId:
					ax.sensorId ?? (props?.sensorId as string | undefined),
				priority:
					ax.priority ?? (props?.priority as string | undefined),
				createDate:
					ax.createDate ?? (props?.createDate as string | undefined),
				sequence: ax.sequence ?? props?.sequence,
			} as SelectedAsset;
		},
		[layerApi, parseAssetIdentity],
	);

	// Re-enrich when layerApi reference changes (i.e. when layers finish loading)
	// so URL-deep-linked selections get their assetType filled from layer config.
	useEffect(() => {
		setSelectedAssets(prev => prev.map(enrichSelectedAsset));
		setHighlightedAssets(prev => prev.map(enrichSelectedAsset));
	}, [enrichSelectedAsset]);

	// hitTest against the VectorTileLayer; returns all matched graphics with layerId + assetId.
	const getHitTestResults = useCallback(
		async (event: any): Promise<SelectedAsset[]> => {
			const v = viewRef.current;
			if (!v || !v.map?.layers.length) return [];

			const response = await v.hitTest(event as any, {
				include: v.map?.layers,
			});

			const results: SelectedAsset[] =
				EsriUtils.viewHitTestResultsToSelectedAssets(response);
			return results.map(enrichSelectedAsset);
		},
		[enrichSelectedAsset],
	);

	// Returns the first hit from getHitTestResults, or null if none.
	const hitTestFirst = useCallback(
		async (event: any): Promise<SelectedAsset | null> => {
			const results = await getHitTestResults(event);
			return results[0] ?? null;
		},
		[getHitTestResults],
	);

	// Wire up click and pointer-move listeners whenever view is ready.
	useEffect(() => {
		if (!view) return;
		const selectionEnabled = functionalityEnabled(MapFunction.selection, displayMode);
		const markerEnabled = functionalityEnabled(MapFunction.marker, displayMode);
		const interactiveEnabled = functionalityEnabled(MapFunction.interactive, displayMode);
		if (!selectionEnabled && !markerEnabled && !interactiveEnabled) return;

		const clickHandle = view.on('click', async event => {
			if (isDrawing) return;
			const features = await getHitTestResults(event);

			if (
				shouldZoomOnClick(
					features.length,
					view.zoom,
					selectionOptions,
				)
			) {
				const newZoom = Math.max(
					view.zoom + 1,
					selectionOptions.minZoom,
				);

				let center = event.mapPoint ?? view.center;
				if (features.length > 0) {
					// SelectedAsset.geometry is always GeoJSON (via esriGeometryToGeoJSON), so
					// native Esri geometries with x/y are already excluded. Guard on 'coordinates'
					// confirms we have valid GeoJSON with position arrays.
					const geometries = features
						.map(f => f.geometry)
						.filter((g): g is ValidGeometry => !!g && 'coordinates' in g);
					if (geometries.length > 0) {
						const coords = extractLngLatPositions(geometries);
						const bounds = convertCoordsToBounds(coords);
						if (!boundsAreNotInitialized(bounds)) {
							const extent = EsriUtils.boundsToExtent(bounds);
							center = extent.center;
						}
					}
				}

				void view.goTo({ center, zoom: newZoom });
				setSelectedAssets([]);
				return;
			}

			const native = (event as { native?: MouseEvent }).native;
			const isMultiSelect =
				!!native && (native.ctrlKey || native.metaKey);

			if (markerEnabled && !isMultiSelect) {
				if (features.length === 0) {
					if (geocoderPinShownRef.current) {
						clearGeocoderPopup();
					} else {
						const { longitude, latitude } = event.mapPoint;
						if (longitude == null || latitude == null) return;
						geocoderPinShownRef.current = true;
						addGeocoderPin([[longitude, latitude]]);
						const clickX = event.x;
						const clickY = event.y;
						const center: [number, number] = [longitude, latitude];
						void locator
							.locationToAddress(WORLD_GEOCODER_URL, {
								location: event.mapPoint,
							})
							.then(result => {
								const loc = result.location as {
									longitude: number;
									latitude: number;
								} | null | undefined;
								const addressCenter: [number, number] | undefined =
									loc?.longitude != null && loc?.latitude != null
										? [loc.longitude, loc.latitude]
										: undefined;
								setGeocoderPopupProps({
									point: { x: clickX, y: clickY },
									features: [
										{
											place_name: result.address as string,
											center,
											addressCenter,
										},
									],
								});
							})
							.catch(() => {
								setGeocoderPopupProps({
									point: { x: clickX, y: clickY },
									features: [{ center }],
								});
							});
					}
				} else {
					clearGeocoderPopup();
				}
			}

			if (!selectionEnabled) return;

			setSelectedAssets(prev => {
				if (!isMultiSelect) return features;

				if (!features.length) return prev;

				const next = [...prev];
				features.forEach(hit => {
					const index = next.findIndex(
						item =>
							item.id === hit.id && item.layerId === hit.layerId,
					);
					if (index >= 0) {
						next.splice(index, 1);
					} else {
						next.push(hit);
					}
				});

				return next;
			});

			if (features.length) {
				onAssetClickCb.current?.(features[0]);
			}
		});

		const setCursor = (cursor: string) => {
			if (view?.container) {
				view.container.style.cursor = cursor;
			}
		};

		const pointerMoveHandle = view.on('pointer-move', (event: any) => {
			if (isDrawing) {
				cursorRequestIdRef.current += 1;
				setCursor('');
				if (lastHoveredAssetKey.current !== null) {
					lastHoveredAssetKey.current = null;
					onAssetHoverCb.current?.(null);
				}
				return;
			}

			const currentId = ++cursorRequestIdRef.current;
			void getHitTestResults(event).then(hits => {
				if (currentId !== cursorRequestIdRef.current) return;
				const showZoomIn = shouldZoomOnClick(
					hits.length,
					view.zoom,
					selectionOptions,
				);

				const hovered = hits[0] ?? null;
				const hoveredKey = hovered
					? `${hovered.layerId}:${hovered.id}`
					: null;
				if (hoveredKey !== lastHoveredAssetKey.current) {
					lastHoveredAssetKey.current = hoveredKey;
					onAssetHoverCb.current?.(hovered);
				}

				if (hits.length === 0) {
					setCursor(interactiveEnabled ? 'grab' : '');
				} else if (showZoomIn) {
					setCursor('zoom-in');
				} else {
					setCursor('pointer');
				}
			});
		});

		const pointerLeaveHandle = view.on('pointer-leave', () => {
			cursorRequestIdRef.current += 1;
			setCursor('');
			if (lastHoveredAssetKey.current !== null) {
				lastHoveredAssetKey.current = null;
				onAssetHoverCb.current?.(null);
			}
		});

		return () => {
			clickHandle.remove();
			pointerMoveHandle.remove();
			pointerLeaveHandle.remove();
		};
	}, [
		view,
		displayMode,
		getHitTestResults,
		selectionOptions,
		isDrawing,
	]);

	// Sync selectedAssets to EsriLayerManager via useLayers (per-layer selection).
	useEffect(() => {
		if (!layerApi) return;
		if (selectedAssets.length === 0) {
			layerApi.clearSelection();
			return;
		}
		layerApi.clearSelection();
		const queryableLayers = layerApi.getQueryableLayers();
		const queryableLayerIds = new Set(queryableLayers.map(l => l.layerId));
		const byLayer = selectedAssets.reduce<Record<string, string[]>>(
			(acc, a) => {
				// If the hit layer is an overlay (e.g. risk/theme) rather than a main
				// asset layer, remap to the underlying asset layer using assetType so
				// the selection highlight appears on the correct styled layer.
				let resolvedLayerId = a.layerId;
				if (!queryableLayerIds.has(a.layerId) && a.assetType) {
					const match = queryableLayers.find(
						l => l.assetType === a.assetType,
					);
					if (match) resolvedLayerId = match.layerId;
				}
				const list = acc[resolvedLayerId] ?? [];
				list.push(a.id);
				acc[resolvedLayerId] = list;
				return acc;
			},
			{},
		);
		for (const [layerId, ids] of Object.entries(byLayer)) {
			if (layerId && ids.length) {
				layerApi.setSelectedItems(layerId, ids);
			}
		}
	}, [layerApi, selectedAssets]);

	// Sync highlightedAssets to EsriLayerManager (per-layer highlight filter).
	useEffect(() => {
		if (!layerApi) return;
		if (highlightedAssets.length === 0) {
			layerApi.clearHighlight();
			return;
		}
		layerApi.clearHighlight();
		const queryableLayers = layerApi.getQueryableLayers();
		const queryableLayerIds = new Set(queryableLayers.map(l => l.layerId));
		const byLayer = highlightedAssets.reduce<Record<string, string[]>>(
			(acc, a) => {
				// Property pane items can originate from thematic/overlay layers
				// (risk/rehab/etc). Remap those hovers to the base queryable layer
				// so highlighted geometry is visible on the map.
				let resolvedLayerId = a.layerId;
				if (!queryableLayerIds.has(a.layerId) && a.assetType) {
					const match = queryableLayers.find(
						l => l.assetType === a.assetType,
					);
					if (match) resolvedLayerId = match.layerId;
				}
				const list = acc[resolvedLayerId] ?? [];
				list.push(a.id);
				acc[resolvedLayerId] = list;
				return acc;
			},
			{},
		);
		for (const [layerId, ids] of Object.entries(byLayer)) {
			if (layerId && ids.length)
				layerApi.setHighlightedItems(layerId, ids);
		}
	}, [layerApi, highlightedAssets]);

	const selectedAssetIds = useMemo(
		() => selectedAssets.map(a => a.id),
		[selectedAssets],
	);
	const selectedLayerId = useMemo(
		() => selectedAssets[0]?.layerId ?? null,
		[selectedAssets],
	);

	const highlightedAssetIds = useMemo(
		() => highlightedAssets.map(a => a.id),
		[highlightedAssets],
	);
	const highlightedLayerId = useMemo(
		() => highlightedAssets[0]?.layerId ?? null,
		[highlightedAssets],
	);
	const highlightedAssetId = useMemo(
		() => highlightedAssets[0]?.id ?? null,
		[highlightedAssets],
	);

	// Sync externally-provided selectedAssets prop into internal state.
	const prevExternalKey = useRef<string>('');
	useEffect(() => {
		const external = options.selectedAssets;
		if (!external) return;
		const key = external
			.map(a => `${a.layerId ?? ''}:${a.id}`)
			.sort()
			.join('|');
		if (key === prevExternalKey.current) return;
		prevExternalKey.current = key;
		const mapped = external.map(a => {
			const parsed = parseAssetIdentity(a.id);
			return {
				...a,
				...parsed,
				assetType: a.assetType ?? parsed.assetType,
				id: a.id,
				layerId: a.layerId,
			};
		});
		setSelectedAssets(mapped.map(enrichSelectedAsset));
	}, [options.selectedAssets, parseAssetIdentity, enrichSelectedAsset]);

	return {
		selectedAssets,
		selectedAssetIds,
		selectedLayerId,

		highlightedAssets,
		highlightedAssetIds,
		highlightedLayerId,
		highlightedAssetId,

		geocoderPopupProps,
		clearGeocoderPopup,
		setPreviewPin,
		clearPreviewPin,

		getHitTestResults,
		hitTestFirst,

		selectAsset(
			assetId: string,
			zoomTo = false,
			layerId?: string | null,
		): void {
			const identity = parseAssetIdentity(assetId);
			setSelectedAssets(prev => {
				const lid = layerId ?? prev[0]?.layerId ?? '';
				return [
					enrichSelectedAsset({
						id: assetId,
						layerId: lid,
						assetId: identity.assetId,
						assetType: identity.assetType,
					} as SelectedAsset),
				];
			});
			if (zoomTo) {
				this.zoomToAsset(assetId);
			}
		},

		selectAssets(
			assetIds: string[],
			spanExtent = false,
			layerId?: string | null,
		): void {
			setSelectedAssets(prev => {
				const lid = layerId ?? prev[0]?.layerId ?? '';
				return assetIds.map(id => {
					const identity = parseAssetIdentity(id);
					return enrichSelectedAsset({
						id,
						layerId: lid,
						assetId: identity.assetId,
						assetType: identity.assetType,
					} as SelectedAsset);
				});
			});
			if (spanExtent) {
				this.zoomToAssets(assetIds);
			}
		},

		deselectAsset(assetId: string): void {
			setSelectedAssets(prev => prev.filter(a => a.id !== assetId));
		},

		clearSelection(): void {
			setSelectedAssets([]);
			layerApi?.clearSelection();
			addPinsToMap(null);
		},

		highlightAsset(assetId: string, layerId?: string | null): void {
			const identity = parseAssetIdentity(assetId);
			setHighlightedAssets(prev => [
				enrichSelectedAsset({
					id: assetId,
					layerId:
						layerId ?? prev[0]?.layerId ?? selectedLayerId ?? '',
					assetId: identity.assetId,
					assetType: identity.assetType,
				} as SelectedAsset),
			]);
		},

		// Each item carries its own layerId instead of one layerId for the
		// whole call. Needed when the same asset id exists on more than one
		// layer - highlighting must not collapse to a single layer or
		// matches on the other layer silently fail to highlight.
		// Keep the legacy highlightAssets signature as a thin adapter so
		// existing callers/tests don't break while we migrate to per-layer
		// highlighting.
		highlightAssets(assetIds: string[], layerId?: string | null): void {
			this.highlightAssetsPerLayer(
				assetIds.map(id => ({ id, layerId: layerId ?? null })),
			);
		},

		highlightAssetsPerLayer(
			items: Array<{ id: string; layerId?: string | null }>,
		): void {
			setHighlightedAssets(prev =>
				items.map(({ id, layerId }) => {
					const identity = parseAssetIdentity(id);
					const lid =
						layerId ?? prev[0]?.layerId ?? selectedLayerId ?? '';
					return enrichSelectedAsset({
						id,
						layerId: lid,
						assetId: identity.assetId,
						assetType: identity.assetType,
					} as SelectedAsset);
				}),
			);
		},

		unhighlightAsset(assetId: string): void {
			setHighlightedAssets(prev => prev.filter(a => a.id !== assetId));
		},

		clearHighlight(): void {
			setHighlightedAssets([]);
			layerApi?.clearHighlight();
		},

		zoomToAsset(_assetId: string): void {
			// VectorTileLayer doesn't support queryFeatures.
			// Consumers should pass a geometry directly via zoomToGeometry,
			// or wire up a FeatureLayer query here when ready.
			if (_assetId) {
				this.zoomToAssets([_assetId]);
			}
		},

		zoomToAssets(
			assetIds: string[],
			geometries?: GeoJSON.Geometry[],
		): void {
			const v = viewRef.current;
			if (!v || !assetIds.length) return;
			if (geometries?.length) {
				const valid = geometries.filter((g): g is ValidGeometry => {
					return !!g && 'coordinates' in g;
				});
				if (!valid.length) return;
				const coords = extractLngLatPositions(valid);
				const bounds = convertCoordsToBounds(coords);
				if (boundsAreNotInitialized(bounds)) return;
				if (bounds.getNorth() === bounds.getSouth()) {
					v.goTo({
						target: EsriUtils.lngLatToPoint(bounds.getCenter()),
						zoom: 15,
					});
				} else {
					v.goTo(EsriUtils.boundsToExtent(bounds));
				}

				addPinsToMap(centersForDropPinsFromGeometries(geometries));
				return;
			}
		},

		zoomToLngLat(lng: number, lat: number, zoom?: number): void {
			const v = viewRef.current;
			if (!v) return;
			const center = EsriUtils.lngLatToCenter({ lng, lat });
			v.goTo({
				center,
				zoom: zoom ?? v.zoom,
			});
			addPinsToMap([center] as [number, number][]);
		},

		zoomToGeometry(geometry: GeoJSON.Geometry, zoom?: number): void {
			const v = viewRef.current;
			if (!v) return;
			const arcgisGeometry = geojsonToArcGIS(geometry);
			v.goTo({
				target: arcgisGeometry as GeometryUnion,
				zoom: zoom ?? v.zoom,
			});
		},

		findAssetInNetwork(_assetId: string): void {
			// TODO: query network layer → view.goTo + dispatch event to host app
		},

		onSelectionChange(_cb: (assetIds: string[]) => void): void {
			// TODO: subscribe to internal selection change event
		},

		onAssetClick(cb: (asset: SelectedAsset) => void): void {
			onAssetClickCb.current = cb;
		},

		onAssetHover(cb: (asset: SelectedAsset | null) => void): void {
			onAssetHoverCb.current = cb;
		},
	};
};

export type Selection = ReturnType<typeof useSelection>;
